import React, { useEffect, useRef } from 'react'
import classNames from 'classnames'
import { useWindowSize } from 'usehooks-ts'
import {
  AFFINITY_ICONS,
  LONG_TEXT_CHARACTER_COUNT,
} from './Preference.constants'
import { adjustSizes } from './Preference.utils'
import styles from './Preference.styles.scss'

export interface PreferenceProps {
  preference: {
    affinity: 'loves' | 'needs'
    name: string
  }
}

const Preference: React.FC<PreferenceProps> = ({ preference }) => {
  const { width } = useWindowSize()
  const wrapperRef = useRef<HTMLDivElement>(null)
  const headingRef = useRef<HTMLDivElement>(null)
  const iconRef = useRef<HTMLImageElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const isLongText = (preference.name?.length ?? 0) > LONG_TEXT_CHARACTER_COUNT

  useEffect(() => {
    if (typeof window !== 'object') return

    if (
      !wrapperRef.current ||
      !headingRef.current ||
      !contentRef.current ||
      !iconRef.current
    ) {
      return
    }

    adjustSizes({
      width,
      wrapperRef,
      headingRef,
      iconRef,
      contentRef,
    })
  }, [width])

  const preferenceClasses = classNames(
    styles.Preference,
    styles[preference.affinity]
  )
  const contentClasses = classNames(
    styles.content,
    isLongText && styles.content__longText
  )

  return (
    <div
      className={preferenceClasses}
      data-testid="Preference"
      key={`${preference.affinity}-${preference.name}`}
      ref={wrapperRef}
    >
      <div
        className={styles.heading}
        data-testid="Preference-heading"
        ref={headingRef}
      >
        <img
          alt={AFFINITY_ICONS[preference.affinity].alt}
          data-testid="Preference-heading-icon"
          ref={iconRef}
          role="presentation"
          src={AFFINITY_ICONS[preference.affinity].url}
        />
        {preference.affinity}
      </div>
      <div
        className={contentClasses}
        data-testid="Preference-content"
        ref={contentRef}
      >
        {preference.name}
      </div>
    </div>
  )
}

export default Preference
